import type {
  CoreSvcOrgprincipalsIdentityDTO,
  CoreSvcOrgprincipalsOrgPrincipalWithRolesDTO,
} from '../../../generated/core-svc';

/**
 * Identity types whose remote id can stand in for a missing principal email, best first. A SCIM or
 * SAML remote id is the user name the IdP provisioned (normally the work email) and an email-code
 * identity's remote id is the email itself. Anything else, such as a firebase uid, is opaque and
 * only shown when nothing better exists.
 */
const REMOTE_ID_TYPE_PRIORITY = ['scim', 'saml', 'emailcode'];

export interface RemoteIdentity {
  remoteId: string;
  type: string;
}

export interface OrgUserRole {
  id: string;
  name: string;
}

/** One row of the org users table and of its CSV export. */
export interface OrgUserRow {
  id: string;
  name: string;
  /** Sort key for the Name column: the name with its last word moved first ({@link lastNameSortKey}). */
  sortName: string;
  /** The principal's own email; empty for SSO-provisioned users who have never signed in with one. */
  email: string;
  /** Display form of the remote id picked by {@link pickRemoteIdentity}; empty when there is none. */
  remoteId: string;
  /** Type of the identity `remoteId` came from, e.g. `scim` or `saml`; empty when there is none. */
  remoteIdType: string;
  /** What the "Email / Remote Id" column shows: the email when there is one, else the remote id. */
  emailOrRemoteId: string;
  role: OrgUserRole[];
  assertedIdentity: string | null | undefined;
  walletIds: string[];
  identities: CoreSvcOrgprincipalsIdentityDTO[];
  lastLogin: string | undefined;
}

export type OrgPrincipal = Pick<
  CoreSvcOrgprincipalsOrgPrincipalWithRolesDTO,
  'principalId' | 'principalName' | 'principalEmail' | 'roles' | 'assertedIdentity' | 'identities'
> & { lastLogin?: string };

function identityRank(identity: CoreSvcOrgprincipalsIdentityDTO): number[] {
  const typeRank = REMOTE_ID_TYPE_PRIORITY.indexOf((identity.type ?? '').toLowerCase());
  return [
    identity.enabled === false ? 1 : 0,
    typeRank === -1 ? REMOTE_ID_TYPE_PRIORITY.length : typeRank,
    (identity.remoteId ?? '').includes('@') ? 0 : 1,
  ];
}

function compareRanks(a: number[], b: number[]): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

/**
 * Picks the identity whose remote id best identifies a user without a principal email: an enabled
 * identity before a disabled one, SCIM/SAML/email-code before anything else, and an email-shaped id
 * before an opaque one. Returns null when no identity carries a remote id.
 */
export function pickRemoteIdentity(
  identities: CoreSvcOrgprincipalsIdentityDTO[] | null | undefined
): RemoteIdentity | null {
  const candidates = (identities ?? []).filter((identity) => !!identity.remoteId);
  if (candidates.length === 0) return null;

  const best = candidates.reduce((bestSoFar, identity) =>
    compareRanks(identityRank(identity), identityRank(bestSoFar)) < 0 ? identity : bestSoFar
  );
  return { remoteId: best.remoteId as string, type: (best.type ?? '').toLowerCase() };
}

/** Display form of a remote id. FMR provisions `corpid@fmr.com` user names, so that suffix is dropped. */
export function formatRemoteId(remoteId: string | null | undefined): string {
  if (!remoteId) return '';
  return remoteId.replace(/@fmr\.com$/i, '');
}

/** Generational and professional suffixes that follow a last name, compared without dots. */
const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v', 'phd', 'md', 'dds', 'esq', 'cpa']);

function isNameSuffix(word: string): boolean {
  return NAME_SUFFIXES.has(word.replace(/\./g, ''));
}

/**
 * Sort key that orders names by last name, then the rest: "Ada King Lovelace" -> "lovelace ada king".
 * The last word is taken as the last name, skipping trailing suffixes ("Nathan Swaim, Sr." ->
 * "swaim nathan sr."), which go at the end as a tie-break. "Swaim, Nathan" already puts the last name
 * first and sorts as written. A single-word name sorts as itself.
 */
export function lastNameSortKey(name: string | null | undefined): string {
  const lower = (name ?? '').trim().toLowerCase();
  const toWords = (text: string) => text.split(/[\s,]+/).filter(Boolean);

  const commaParts = lower
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (commaParts.length > 1 && !commaParts.slice(1).every((part) => toWords(part).every(isNameSuffix))) {
    return toWords(lower).join(' ');
  }

  const words = toWords(lower);
  const suffixes: string[] = [];
  // keep at least two words so "John V" stays a first name and a last name
  while (words.length > 2 && isNameSuffix(words[words.length - 1])) {
    suffixes.unshift(words.pop() as string);
  }
  if (words.length < 2) return [...words, ...suffixes].join(' ');
  return [words[words.length - 1], ...words.slice(0, -1), ...suffixes].join(' ');
}

/** Maps a core-svc aggregated org principal to the row the users table and its CSV export render. */
export function principalToUserRow(principal: OrgPrincipal): OrgUserRow {
  const identities = principal.identities ?? [];
  const email = principal.principalEmail ?? '';
  const remote = pickRemoteIdentity(identities);
  const remoteId = formatRemoteId(remote?.remoteId);
  const name = principal.principalName ?? '';

  return {
    id: principal.principalId ?? '',
    name,
    sortName: lastNameSortKey(name),
    email,
    remoteId,
    remoteIdType: remote?.type ?? '',
    emailOrRemoteId: email || remoteId,
    role: (principal.roles ?? []).map((role) => ({ id: role.roleId ?? '', name: role.roleName ?? '' })),
    assertedIdentity: principal.assertedIdentity,
    walletIds: [], // not provided by the core-svc principals endpoint
    identities,
    lastLogin: principal.lastLogin,
  };
}
